import './Input.css';

import {
  type ChangeEvent,
  type ComponentProps,
  type FocusEvent,
  type FunctionComponent,
  type ReactNode,
  useState,
} from 'react';
import type { FieldError } from 'react-hook-form';

import type { Icon } from '../Icons/types';

export interface InputProps extends ComponentProps<'input'> {
  LeftIcon?: Icon;
  error?: FieldError | undefined;
  rightElement?: ReactNode;
  isInputActive?: boolean;
}

const INPUT_ICON_SIZE = 24;

export const Input: FunctionComponent<InputProps> = ({
  LeftIcon,
  placeholder,
  onFocus,
  onBlur,
  onChange,
  error,
  rightElement,
  className,
  disabled,
  isInputActive = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState<string | undefined>(undefined);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(event);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value);
    if (onChange) onChange(event);
  };

  const isActive = isFocused || Boolean(value) || isInputActive;

  return (
    <div className="input-error-container">
      <div
        className={`input-container ${isActive ? 'input-active' : ''} ${error !== undefined ? 'input-error' : ''} ${disabled ? 'input-disabled' : ''}`}
        data-testid="input-container"
      >
        {LeftIcon !== undefined && <LeftIcon size={INPUT_ICON_SIZE} />}
        <div className="input-label-container">
          <p
            className={`input-label text-xs regular ${disabled ? 'input-disabled' : ''}`}
          >
            {placeholder}
          </p>
          <input
            {...props}
            placeholder={placeholder}
            className={`input text-base regular ${disabled ? 'input-disabled' : ''} ${className ?? ''}`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
        {rightElement}
      </div>
      {error?.message !== undefined && (
        <p
          data-testid="input-error-text"
          className="text-xs regular input-error-text"
        >
          {error.message}
        </p>
      )}
    </div>
  );
};
